/**
 * Uma página hospedada: a amostra, os arquivos, o endereço e quem vê.
 *
 * ## O desenho: a amostra manda, o resto acompanha
 *
 * A primeira versão empilhava tudo em blocos de largura inteira, na ordem em
 * que foi escrita — e a lista de arquivos, que pode ter quarenta linhas,
 * empurrava a pré-visualização para fora da tela. Quem abria o projeto via uma
 * lista de nomes de arquivo, não a página.
 *
 * Agora são duas colunas: a amostra à esquerda, grande e **grudada** na
 * rolagem; os controles à direita, numa coluna estreita que rola junto com a
 * lista de arquivos. Em tela estreita viram uma coluna só, com a amostra
 * primeiro — que continua sendo o que a pessoa veio ver.
 *
 * ## A pré-visualização usa a MESMA contenção da página publicada
 *
 * O `iframe` aponta para o `contentBase` que a API devolveu — que num projeto
 * não público já traz o crachá de leitura, sem o qual o CSS e o JS não
 * carregariam. O servidor entrega esse prefixo com
 * entrega com `Content-Security-Policy: sandbox allow-scripts` — em **origem
 * opaca**. Uma amostra mais permissiva do que a publicação mostraria algo que
 * depois não funciona, e abriria a nossa origem para o conteúdo dela dentro de
 * uma tela autenticada.
 *
 * `allow-same-origin` **não** entra no atributo `sandbox`. Com ele, o HTML do
 * usuário voltaria a rodar como nós — lendo o cookie de sessão e chamando
 * `/api` como quem está olhando. Há teste que varre o código e reprova se o
 * token aparecer fora de comentário, e outro que exige o `sandbox` em todo
 * `iframe` do front: apagá-lo tem o mesmo efeito que afrouxá-lo.
 *
 * ## Por que a moldura não é reaproveitada aqui
 *
 * `/pagina-<uuid>` já é uma página nossa com faixa de procedência e o conteúdo
 * dentro. Embuti-la daria moldura dentro de moldura, com a faixa repetida.
 * Dentro do painel a procedência é óbvia — é o projeto da própria pessoa.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { AppShell, initialsOf } from '../components/AppShell.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { Toast, notify } from '../components/Toast.jsx';
import { api, ApiError, SessionError, goToSignIn, photoUrl } from '../api.js';
import { AjudaDoTour } from '../tour/AjudaDoTour.jsx';
import { deInput, deSoltar } from '../siteUpload.js';
import { VISIBILIDADE, emMB, urlDoConteudo } from './Sites.jsx';

const ehPagina = (arquivo) => /^text\/html/.test(arquivo.contentType);

/* ── cabeçalho editável ──────────────────────────────────────── */

/**
 * A janela de editar nome e descrição.
 *
 * ## Por que janela, e não o formulário no lugar
 *
 * Até 21/10 o formulário **se abria dentro do cabeçalho**, empurrando a
 * pré-visualização e a coluna inteira para baixo. Quem clicava em Editar via a
 * tela saltar, e quem cancelava via saltar de volta — para mudar dois campos
 * que não têm relação nenhuma com o resto da página.
 *
 * A janela resolve os dois: nada se move atrás dela, e a tarefa fica isolada
 * do que está em volta. É também o idioma que o resto da plataforma já usa
 * para edição focada — criar artefato, ligar grupo, examinar conteúdo.
 */
function EditarIdentidade({ site, onClose, onSaved }) {
  const [name, setName] = useState(site.name);
  const [description, setDescription] = useState(site.description ?? '');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const { site: novo } = await api.sites.save(site.id, {
        name: name.trim(),
        // `null` apaga a descrição; string vazia seria uma descrição em branco,
        // que a lista desenharia como uma linha vazia.
        description: description.trim() || null,
      });
      notify('Artefato atualizado.');
      onSaved(novo);
      onClose();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setSalvando(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editar-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <div>
            <h2 id="editar-titulo" className="panel-title">
              Editar artefato
            </h2>
            <p className="panel-desc">Como ele aparece na sua lista e para quem abrir o link.</p>
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <form className="panel-body editor-body" onSubmit={salvar}>
          <div className="field">
            <label className="field-label" htmlFor="ed-nome">
              Nome
            </label>
            <input
              id="ed-nome"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
              /* O foco começa aqui: quem abriu a janela veio mudar o nome na
                 maioria das vezes, e obrigar a clicar antes de digitar é um
                 passo que a janela deveria ter poupado. */
              autoFocus
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="ed-desc">
              Descrição <span className="optional">opcional</span>
            </label>
            <textarea
              id="ed-desc"
              className="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Para que serve, e para quem"
            />
          </div>

          <div className="panel-actions">
            <button
              type="submit"
              className="button primary"
              disabled={salvando || name.trim().length < 2}
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="button secondary" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cabecalho({ site, onSaved }) {
  const [editando, setEditando] = useState(false);
  const v = VISIBILIDADE[site.visibility] ?? { label: site.visibility, cls: 'neutral' };

  return (
    <header className="site-head">
      <Link className="thread-back" to="/artefatos">
        ← Meus artefatos
      </Link>

      <div className="site-head-row">
        <h1 className="site-title">{site.name}</h1>
        <span className={`status-pill ${v.cls}`}>{v.label}</span>

        {/* Empurrado para a direita pelo CSS: o nome é o assunto da linha, e
            um botão colado nele disputa a leitura com o título. */}
        <button
          type="button"
          className="button ghost site-head-editar"
          onClick={() => setEditando(true)}
        >
          Editar
        </button>
      </div>

      {site.description ? (
        <p className="site-head-desc">{site.description}</p>
      ) : (
        /* Era "Sem descrição." — uma constatação. Vira um convite, e clicável:
           o que falta e o que resolve ficam no mesmo lugar. */
        <button type="button" className="site-head-desc is-empty" onClick={() => setEditando(true)}>
          Adicionar uma descrição
        </button>
      )}

      {editando && (
        <EditarIdentidade site={site} onClose={() => setEditando(false)} onSaved={onSaved} />
      )}
    </header>
  );
}

/* ── endereço ────────────────────────────────────────────────── */

/**
 * Copia o endereço público do artefato.
 *
 * Vive solto porque é usado na barra da pré-visualização, onde o endereço
 * deixou de ter painel próprio em 21/10 — o quadro com a URL escrita saiu, e o
 * que ele oferecia de útil (copiar e abrir) virou dois botões ao lado da
 * amostra, que é onde se está quando se quer mandar o link para alguém.
 */
async function copiarEndereco(site) {
  const url = `${window.location.origin}${site.path}`;
  try {
    await navigator.clipboard.writeText(url);
    notify('Endereço copiado.');
  } catch {
    // Área de transferência negada (sem HTTPS, ou permissão recusada).
    // Dizer o que fazer é melhor que falhar calado.
    notify(`Não consegui copiar. O endereço é ${url}`, 'erro');
  }
}

/*
 * `TrocarEndereco` foi removido em 21/10, a pedido.
 *
 * Ele existia para cortar quem já tinha o link, e isso fazia sentido enquanto
 * o alcance era `PUBLICO`: lá **o endereço ERA a credencial**, e invalidá-lo
 * era a única forma de despublicar o que já havia circulado.
 *
 * Com o compartilhamento por pessoa, quem decide é a identidade: tirar alguém
 * da lista corta o acesso tendo ou não o link. O botão passou a oferecer uma
 * proteção que a lista já dá, com o custo de quebrar os links de todo mundo.
 *
 * **O que se perdeu, e vale saber se alguém pedir de volta:** trocar o slug
 * invalidava também os crachás de conteúdo em circulação, já que o caminho os
 * contém. Hoje um crachá repassado vale até vencer — uma hora, ver
 * `tokenDeConteudo.js`. A rota `POST /api/sites/:id/endereco` continua de pé no
 * servidor; o que saiu foi a porta na tela.
 */

/* ── visibilidade ────────────────────────────────────────────── */

/**
 * A janela de escolher com quem.
 *
 * ## Lista inteira, e não só busca
 *
 * Sete pessoas hoje, e a busca filtra. Com a lista à vista, compartilhar é
 * reconhecer um nome; só com busca, é lembrar de um. O campo existe para
 * quando a lista crescer, e para o caso que a lista não cobre — quem **ainda
 * não entrou** no AI Hub e por isso não está nela.
 *
 * ## Convidar quem não existe aqui é o mesmo ato
 *
 * O vínculo é por e-mail, não por conta. Digitar um endereço que a plataforma
 * nunca viu grava o compartilhamento do mesmo jeito, e ele espera: quando a
 * pessoa entrar pela primeira vez, o artefato já está na lista dela. Não há
 * convite pendente para aceitar, nem passo de reivindicação depois.
 */
function EscolherPessoas({ site, pessoas, onClose, onMudou }) {
  const [usuarios, setUsuarios] = useState(null);
  const [busca, setBusca] = useState('');
  const [agindo, setAgindo] = useState(null);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  useEffect(() => {
    let vivo = true;
    api
      .usuarios()
      .then((r) => vivo && setUsuarios(r.usuarios))
      .catch((err) => {
        if (err instanceof SessionError) return goToSignIn();
        return vivo && setUsuarios([]);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const jaTem = useMemo(() => new Set(pessoas.map((p) => p.upn)), [pessoas]);

  /**
   * Todo mundo que pode aparecer aqui — e quem já tem acesso vem primeiro.
   *
   * ## Sem isto, compartilhar com alguém de fora do hub era irreversível
   *
   * A lista vinha só de `app_user`, que são as pessoas que já entraram. Quem
   * foi convidado por e-mail e ainda não chegou **não estava em lugar nenhum
   * do seletor**: o painel lateral mostrava o nome, sem ação, e não havia como
   * tirar o acesso a não ser por SQL. Foi o usuário que achou, em 21/10, no
   * dia em que o recurso subiu.
   *
   * Quem já tem acesso no topo é consequência disso: numa janela que serve
   * para conceder e para retirar, o que está concedido é o que se vem
   * conferir.
   */
  const candidatos = useMemo(() => {
    const comAcesso = pessoas.map((p) => ({
      oid: p.oid,
      upn: p.upn,
      name: p.name,
      jaEntrou: p.jaEntrou,
      tem: true,
    }));

    const listados = new Set(comAcesso.map((p) => p.upn));
    const resto = (usuarios ?? [])
      .filter((u) => !listados.has(u.upn?.toLowerCase()))
      .map((u) => ({ ...u, jaEntrou: true, tem: false }));

    return [...comAcesso, ...resto];
  }, [pessoas, usuarios]);

  const visiveis = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return candidatos.filter(
      (u) =>
        !t || (u.name ?? '').toLowerCase().includes(t) || (u.upn ?? '').toLowerCase().includes(t),
    );
  }, [candidatos, busca]);

  /** O que foi digitado parece um e-mail que não está entre os usuários? */
  const convite = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t)) return null;
    // Nem para quem já está no seletor, nem para quem já tem acesso: nos dois
    // casos a linha da pessoa já aparece acima, e um segundo botão para a
    // mesma pessoa só confunde.
    if (candidatos.some((u) => u.upn?.toLowerCase() === t)) return null;
    return t;
  }, [busca, candidatos]);

  async function alternar(upn) {
    const tinha = jaTem.has(upn.toLowerCase());
    setAgindo(upn);
    try {
      const r = tinha ? await api.sites.unshare(site.id, upn) : await api.sites.share(site.id, upn);
      onMudou(r);
      if (!tinha) setBusca('');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setAgindo(null);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel compartilhar-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compartilhar-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <div>
            <h2 id="compartilhar-titulo" className="panel-title">
              Compartilhar
            </h2>
            <p className="panel-desc">
              Quem estiver na lista abre o artefato pelo endereço, entrando no AI Hub.
            </p>
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body editor-body">
          <input
            className="input"
            type="search"
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar pessoa"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            autoFocus
          />

          {usuarios === null && <p className="state-message">Carregando…</p>}

          {convite && (
            /* O caso que a lista não cobre: alguém da Iguatemi que ainda não
               entrou aqui. O botão diz isso, para não parecer que a pessoa
               sumiu da busca. */
            <button
              type="button"
              className="compartilhar-convite"
              disabled={agindo !== null}
              onClick={() => alternar(convite)}
            >
              <span className="compartilhar-nome">{convite}</span>
              <span className="cell-sub">Ainda não entrou no AI Hub — recebe assim que entrar</span>
            </button>
          )}

          {usuarios !== null && (
            <div className="compartilhar-lista">
              {visiveis.length === 0 && !convite && (
                <p className="state-message">Ninguém com esse nome.</p>
              )}

              {visiveis.map((u) => (
                /* A chave é o e-mail, não o `oid`: quem foi convidado e ainda
                   não entrou não tem `oid`, e `key={undefined}` faria o React
                   embaralhar as linhas ao conceder ou retirar. */
                <button
                  key={u.upn}
                  type="button"
                  className={`compartilhar-pessoa ${u.tem ? 'is-ativa' : ''}`}
                  disabled={agindo !== null}
                  onClick={() => alternar(u.upn)}
                  title={u.tem ? 'Clique para tirar o acesso' : 'Clique para compartilhar'}
                >
                  <Avatar
                    src={u.oid ? photoUrl.usuario(u.oid) : null}
                    initials={initialsOf(u.name, u.upn)}
                    name={u.name}
                    size="sm"
                  />
                  <span className="compartilhar-quem">
                    <span className="compartilhar-nome">{u.name ?? u.upn}</span>
                    <span className="cell-sub">
                      {u.jaEntrou ? u.upn : `${u.upn} · ainda não entrou no AI Hub`}
                    </span>
                  </span>
                  <span className="compartilhar-marca" aria-hidden="true">
                    {u.tem ? '✓' : '+'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Quem pode ver: o alcance, e a lista de pessoas.
 *
 * ## Dois controles, e o terceiro estado é derivado
 *
 * A pessoa escolhe entre **Privado** e **Todo o AI Hub**. `COMPARTILHADO` não
 * é escolha: ele vale exatamente quando há alguém na lista, e o servidor o
 * recalcula a cada mudança. Oferecê-lo como botão criaria o estado
 * "compartilhado com ninguém", que a tela mostraria como aberto e o acesso
 * trataria como fechado — e a divergência só apareceria quando alguém
 * reclamasse de não conseguir abrir.
 *
 * ## Voltar para privado pergunta antes
 *
 * Porque a lista vai junto, e a perda é silenciosa: os nomes somem e nem o
 * dono lembra quem eram. É a única confirmação desta tela que não é sobre
 * apagar arquivo.
 */
function Compartilhamento({ site, onSaved }) {
  const [pessoas, setPessoas] = useState([]);
  const [escolhendo, setEscolhendo] = useState(false);
  const [salvando, setSalvando] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.sites.shares(site.id);
      setPessoas(r.pessoas);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      // Falhar a lista não derruba o painel: o alcance continua legível pelo
      // estado do artefato, que já veio junto com ele.
      notify(err.message, 'erro');
    }
    return undefined;
  }, [site.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function escolher(visibility) {
    if (visibility === site.visibility) return;

    if (visibility === 'PRIVADO' && pessoas.length > 0) {
      const quantas = pessoas.length === 1 ? 'a pessoa' : `as ${pessoas.length} pessoas`;
      const certeza = window.confirm(
        `Voltar para privado tira ${quantas} da lista.\n\n` +
          'Elas deixam de abrir o artefato, e a lista não volta — seria preciso ' +
          'compartilhar de novo, uma a uma.',
      );
      if (!certeza) return;
    }

    setSalvando(visibility);
    try {
      const { site: novo } = await api.sites.save(site.id, { visibility });
      notify(
        visibility === 'HUB'
          ? 'Qualquer pessoa do AI Hub abre este artefato agora.'
          : 'Agora só você vê este artefato.',
      );
      onSaved(novo);
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setSalvando(null);
    }
    return undefined;
  }

  function aplicarMudanca(r) {
    setPessoas(r.pessoas);
    // A visibilidade é derivada no servidor: acrescentar a primeira pessoa
    // move o artefato de PRIVADO para COMPARTILHADO sozinho, e a pastilha do
    // cabeçalho precisa acompanhar.
    if (r.visibility !== site.visibility) onSaved({ ...site, visibility: r.visibility });
  }

  // `HUB` ganha de tudo: a lista continua existindo, mas não decide nada
  // enquanto o alcance for a casa inteira. A tela diz isso, em vez de mostrar
  // uma lista que parece estar controlando o acesso e não está.
  const listaManda = site.visibility !== 'HUB';

  let resumo;
  if (!listaManda) {
    resumo = 'Todo o AI Hub vê este artefato. A lista abaixo volta a valer se escolher Privado.';
  } else if (pessoas.length === 0) {
    resumo = 'Ninguém além de você. Compartilhe para liberar pessoa por pessoa.';
  } else {
    resumo =
      pessoas.length === 1 ? '1 pessoa tem acesso.' : `${pessoas.length} pessoas têm acesso.`;
  }

  return (
    <section className="site-block">
      <div className="section-label">
        Quem pode ver
        <AjudaDoTour tour="visibilidade" rotulo="O que cada alcance significa" />
      </div>

      <div
        className="site-visibility"
        role="radiogroup"
        aria-label="Quem pode ver"
        data-tour="artefato-visibilidade"
      >
        {['PRIVADO', 'HUB'].map((id) => {
          const v = VISIBILIDADE[id];
          // PRIVADO fica marcado também em COMPARTILHADO: o alcance escolhido
          // continua sendo "ninguém além de quem eu disser".
          const ativo = id === 'HUB' ? site.visibility === 'HUB' : site.visibility !== 'HUB';
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={ativo}
              className={`site-vis-option ${ativo ? 'is-active' : ''}`}
              onClick={() => escolher(id)}
              disabled={salvando !== null}
            >
              <span className="site-vis-label">{v.label}</span>
              <span className="site-vis-hint">{v.hint}</span>
            </button>
          );
        })}
      </div>

      <div className="compartilhar-resumo">
        <p className="field-hint">{resumo}</p>

        {pessoas.length > 0 && (
          <ul className="compartilhar-quem-tem">
            {pessoas.map((p) => (
              <li key={p.upn}>
                {/* Sem `oid` não há foto: é quem ainda não entrou no AI Hub, e
                    `Avatar` cai nas iniciais sozinho quando `src` é nulo. */}
                <Avatar
                  src={p.oid ? photoUrl.usuario(p.oid) : null}
                  initials={initialsOf(p.name, p.upn)}
                  name={p.name}
                  size="sm"
                />
                <span className="compartilhar-quem">
                  <span className="compartilhar-nome">{p.name ?? p.upn}</span>
                  <span className="cell-sub">
                    {p.jaEntrou ? p.upn : `${p.upn} · ainda não entrou no AI Hub`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}

        <button type="button" className="button secondary" onClick={() => setEscolhendo(true)}>
          Compartilhar
        </button>
      </div>

      {escolhendo && (
        <EscolherPessoas
          site={site}
          pessoas={pessoas}
          onClose={() => setEscolhendo(false)}
          onMudou={aplicarMudanca}
        />
      )}
    </section>
  );
}

/* ── arquivos ────────────────────────────────────────────────── */

/* ── apagar ──────────────────────────────────────────────────── */

/**
 * Apagar, nos dois estados da tela.
 *
 * `discreto` é para o projeto recém-criado, onde ainda não há o que perder: a
 * ação existe — criar sem querer ficou mais fácil desde que criar leva direto
 * para cá — mas não compete com o que a pessoa veio fazer.
 */
function Apagar({ onApagar, apagando, setApagando, discreto = false }) {
  if (!apagando) {
    return discreto ? (
      <section className="site-block is-discreto">
        <button type="button" className="button ghost" onClick={() => setApagando(true)}>
          Apagar este artefato
        </button>
      </section>
    ) : (
      <section className="site-block">
        <div className="section-label">Apagar</div>
        <button type="button" className="button ghost" onClick={() => setApagando(true)}>
          Apagar este artefato
        </button>
      </section>
    );
  }

  // Classe base mais modificador, dentro de um template literal: é o idioma
  // da base (`is-active`, `is-grande`) e é o que o teste de contrato entende.
  // Ternário solto em `className` faz o extrator ler o nome da variável como
  // se fosse uma classe.
  return (
    <section className={`site-block ${discreto ? 'is-discreto' : ''}`}>
      {!discreto && <div className="section-label">Apagar</div>}
      <div className="site-danger" role="alert">
        <p>
          <strong>Isto apaga o artefato e todos os arquivos dele.</strong> Não há como desfazer, e o
          endereço para de abrir na hora.
        </p>
        <div className="panel-actions">
          <button type="button" className="button primary" onClick={onApagar}>
            Apagar mesmo assim
          </button>
          <button type="button" className="button secondary" onClick={() => setApagando(false)}>
            Cancelar
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── arquivos ────────────────────────────────────────────────── */

/**
 * @param {boolean} [grande] a variante do projeto vazio: só a zona de soltar,
 *   maior, sem cota e sem lista — não há o que listar, e a cota de um projeto
 *   vazio é informação que não ajuda ninguém a decidir nada.
 */
function Arquivos({ site, onChanged, grande = false }) {
  const [enviando, setEnviando] = useState(null);
  const [arrastando, setArrastando] = useState(false);
  const [falhas, setFalhas] = useState([]);
  const inputArquivos = useRef(null);
  const inputPasta = useRef(null);

  /**
   * Envia um por um, e **não para no primeiro erro**.
   *
   * Sequencial de propósito: o servidor aguenta paralelo (a cota é conferida
   * dentro da transação, com a linha do projeto travada), mas em série dá para
   * dizer "3 de 12" e, mais importante, dizer QUAL arquivo foi recusado. Numa
   * rajada paralela as recusas chegam embaralhadas.
   *
   * Seguir depois de uma falha é o certo aqui: um `.svg` no meio de dez
   * arquivos não é motivo para descartar os outros nove. A lista de recusados
   * fica na tela, com o motivo de cada um.
   */
  async function enviar(itens) {
    if (itens.length === 0) return;

    const recusados = [];
    setFalhas([]);

    for (let i = 0; i < itens.length; i += 1) {
      const { path, file } = itens[i];
      setEnviando({ atual: i + 1, total: itens.length, path });
      try {
        await api.sites.uploadFile(site.id, path, file);
      } catch (err) {
        if (err instanceof SessionError) {
          goToSignIn();
          return;
        }
        recusados.push({ path, motivo: err instanceof ApiError ? err.message : 'Falhou.' });
      }
    }

    setEnviando(null);
    setFalhas(recusados);

    const enviados = itens.length - recusados.length;
    if (enviados > 0) {
      notify(`${enviados} arquivo${enviados > 1 ? 's' : ''} enviado${enviados > 1 ? 's' : ''}.`);
    }
    if (recusados.length > 0 && enviados === 0) notify('Nenhum arquivo foi aceito.', 'erro');

    await onChanged();
  }

  async function remover(path) {
    try {
      await api.sites.deleteFile(site.id, path);
      notify('Arquivo removido.');
      await onChanged();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  const usado = site.totalBytes ?? 0;
  const teto = site.limits?.bytesPorProjeto ?? 0;

  return (
    <section className={`site-block ${grande ? 'is-envio' : ''}`}>
      {!grande && (
        <div className="site-block-head">
          <div className="section-label">Arquivos</div>
          <span className="site-quota">
            {site.fileCount ?? 0}/{site.limits?.arquivos ?? '—'} · {emMB(usado)} de {emMB(teto)}
          </span>
        </div>
      )}

      {/* A zona inteira é um alvo de soltar. O `<div>` não é interativo por
          teclado de propósito: os dois botões dentro dele fazem a mesma coisa,
          e são eles que recebem foco. */}
      <div
        className={`site-drop ${grande ? 'is-grande' : ''} ${arrastando ? 'is-active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setArrastando(false);
          await enviar(await deSoltar(e.dataTransfer));
        }}
      >
        <p className="site-drop-text">Arraste os arquivos ou a pasta do protótipo</p>
        <div className="panel-actions">
          <button
            type="button"
            className="button secondary"
            onClick={() => inputArquivos.current?.click()}
          >
            Arquivos
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={() => inputPasta.current?.click()}
          >
            Pasta
          </button>
        </div>
        <p className="field-hint">
          HTML, CSS, JS, JSON, TXT e imagens. Até {emMB(site.limits?.bytesPorArquivo ?? 0)} cada.
          SVG não é aceito.
        </p>

        {/* O `value = ''` limpa o campo para que escolher o MESMO arquivo de
            novo volte a disparar `onChange` — sem isso, corrigir um arquivo e
            reenviá-lo não faria nada. O elemento é guardado antes do `await`:
            depois dele o React já pode ter reciclado o evento. */}
        <input
          ref={inputArquivos}
          type="file"
          multiple
          className="visually-hidden"
          onChange={async (e) => {
            const campo = e.target;
            await enviar(deInput(campo.files));
            campo.value = '';
          }}
        />
        {/* `webkitdirectory` é o que preserva `css/estilo.css`. Sem ele, os
            links relativos do protótipo apontariam para o vazio. */}
        <input
          ref={inputPasta}
          type="file"
          webkitdirectory=""
          multiple
          className="visually-hidden"
          onChange={async (e) => {
            const campo = e.target;
            await enviar(deInput(campo.files, true));
            campo.value = '';
          }}
        />
      </div>

      {enviando && (
        <p className="site-sending" role="status">
          Enviando {enviando.atual} de {enviando.total} — <code>{enviando.path}</code>
        </p>
      )}

      {falhas.length > 0 && (
        <div className="site-danger" role="alert">
          <p>
            <strong>
              {falhas.length} arquivo{falhas.length > 1 ? 's' : ''} não{' '}
              {falhas.length > 1 ? 'foram aceitos' : 'foi aceito'}:
            </strong>
          </p>
          <ul className="site-fail-list">
            {falhas.map((f) => (
              <li key={f.path}>
                <code>{f.path}</code> — {f.motivo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {grande ? null : site.files?.length === 0 ? (
        <p className="state-message">
          Nenhum arquivo ainda. O artefato só abre depois que você enviar um HTML.
        </p>
      ) : (
        /* Rolável: um protótipo de quarenta arquivos empurraria tudo o que vem
           depois para fora da tela. */
        <ul className="site-files">
          {site.files?.map((a) => (
            <li className="site-file" key={a.path}>
              <span className="site-file-path">
                {a.path}
                {a.path === site.entryPath && <span className="tag">entrada</span>}
              </span>
              <span className="site-file-size">{emMB(a.sizeBytes)}</span>
              <button
                type="button"
                className="button ghost"
                onClick={() => remover(a.path)}
                aria-label={`Remover ${a.path}`}
              >
                Remover
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── a tela ──────────────────────────────────────────────────── */

/** De quanto em quanto tempo a tela confere se o projeto mudou noutro lugar. */
const INTERVALO_MS = 15_000;

/**
 * Quando renovar o crachá do quadro, ainda que nada tenha mudado.
 *
 * O crachá vale **uma hora** desde 21/10 (`tokenDeConteudo.js`, que explica por
 * que encurtou). Quarenta e cinco minutos é a folga: renova antes de vencer, e
 * raro o bastante para quem deixa o painel aberto ver o quadro recarregar uma
 * vez por hora, não a cada quinze segundos — que era o defeito consertado na
 * mesma sessão.
 *
 * Se o prazo do crachá mudar de novo, este número vai junto. Renovar depois do
 * vencimento não é "um pouco tarde": é a navegação dentro do protótipo parando
 * de funcionar sem nenhuma mensagem.
 */
const RENOVAR_CRACHA_MS = 45 * 60 * 1000;

export function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apagando, setApagando] = useState(false);

  /**
   * O que o quadro está mostrando: o endereço base e o selo que o força a
   * recarregar.
   *
   * ## Os dois juntos, e não o selo sozinho — o defeito de 21/10
   *
   * O conteúdo é servido `no-store`, mas um `iframe` já desenhado não volta ao
   * servidor sozinho: ele só recarrega se o `src` mudar. O selo entra na URL
   * como `?v=`, que a rota ignora — ele existe para o navegador.
   *
   * O que faltava era o **endereço base** ficar aqui junto. Em projeto que não
   * é público, `contentBase` carrega um crachá HMAC, e `emitir()` o assina com
   * `Date.now()`: **cada leitura da API devolve um crachá diferente**. Como a
   * verificação periódica relia o projeto a cada quinze segundos e gravava o
   * resultado em `site`, o `src` do quadro mudava sozinho — e a página
   * recarregava embaixo de quem estava lendo, sem nada ter mudado nela.
   *
   * O `key` do `iframe` não pegava isso: ele só olhava entrada e selo, que
   * continuavam iguais. O React mantinha o elemento e trocava o atributo
   * `src`, que é justamente o que faz o navegador navegar.
   *
   * Fixando o base aqui, ele só muda quando alguém decide que o quadro deve
   * recarregar.
   */
  const [quadro, setQuadro] = useState(null);

  /**
   * Manda o quadro recarregar, com o endereço que vier.
   *
   * `em` registra quando o crachá foi fixado — ele vale quatro horas, e um
   * painel aberto a tarde inteira ficaria com um crachá vencido. A verificação
   * periódica renova antes disso; sem o carimbo, a primeira navegação dentro
   * do protótipo depois do vencimento falharia em silêncio.
   */
  const recarregarQuadro = useCallback((base) => {
    if (!base) return;
    setQuadro({ base, selo: Date.now(), em: Date.now() });
  }, []);

  usePageMeta(site ? `AI Hub — ${site.name}` : 'AI Hub — página');

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.sites.get(id);
      setSite(r.site);
      return r.site;
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, [id]);

  useEffect(() => {
    load().then((novo) => novo && recarregarQuadro(novo.contentBase));
  }, [load, recarregarQuadro]);

  /** Depois de mexer nos arquivos: relê o projeto e recarrega a amostra. */
  const recarregar = useCallback(async () => {
    const novo = await load();
    if (novo) recarregarQuadro(novo.contentBase);
  }, [load, recarregarQuadro]);

  /**
   * A amostra se atualiza sozinha.
   *
   * O caso que isto cobre não é o envio pela própria tela — esse já recarrega
   * na hora. É o projeto mudar **em outro lugar**: outra aba aberta no mesmo
   * projeto, ou a pessoa reenviando um arquivo pelo celular enquanto o painel
   * fica aberto no monitor.
   *
   * ## Não grava nada quando nada mudou
   *
   * `setSite` com um objeto novo a cada quinze segundos era metade do defeito
   * de 21/10 — ver `quadro`. Agora a verificação lê, **compara, e só então**
   * escreve. Sem mudança, a tela não re-renderiza e o quadro não sente nada.
   *
   * E só com a aba visível: recarregar uma página que ninguém está olhando é
   * trabalho jogado fora, do lado do servidor inclusive.
   */
  useEffect(() => {
    if (!site) return undefined;

    const timer = setInterval(async () => {
      if (document.hidden) return;

      let novo;
      try {
        const r = await api.sites.get(id);
        novo = r.site;
      } catch {
        // Falha de rede numa verificação de fundo não merece tela de erro: a
        // próxima tentativa vem em quinze segundos.
        return;
      }

      if (novo.updatedAt !== site.updatedAt) {
        setSite(novo);
        recarregarQuadro(novo.contentBase);
        return;
      }

      // Nada mudou. A única razão para tocar no quadro é o crachá estar perto
      // de vencer — sem isto, um painel aberto por horas para de abrir os
      // links de dentro do próprio protótipo.
      if (quadro && Date.now() - quadro.em > RENOVAR_CRACHA_MS) {
        recarregarQuadro(novo.contentBase);
      }
    }, INTERVALO_MS);

    return () => clearInterval(timer);
  }, [site, id, quadro, recarregarQuadro]);

  /**
   * Depois de salvar um campo: usa o que a rota devolveu.
   *
   * O `PATCH` não devolve `files` — ele responde o projeto, sem a lista.
   * Mesclar em cima do que já está na tela preserva os arquivos; trocar o
   * objeto inteiro faria a lista desaparecer até a próxima leitura.
   */
  const aplicar = useCallback((novo) => {
    setSite((atual) => ({ ...atual, ...novo, files: novo.files ?? atual.files }));
  }, []);

  /**
   * Para as mudanças que afetam o CONTEÚDO servido, e só para elas.
   *
   * Trocar a visibilidade e trocar o endereço mudam a URL de onde o quadro lê
   * — a primeira faz o crachá aparecer ou sumir, a segunda troca o segmento
   * do projeto. Sem recarregar, o quadro continuaria lendo de um endereço que
   * não vale mais.
   *
   * Nome e descrição usam `aplicar` e **não** recarregam: não têm relação
   * nenhuma com o que está desenhado, e recarregar ali fazia a amostra piscar
   * cada vez que alguém corrigia um título.
   */
  const aplicarConteudo = useCallback(
    (novo) => {
      aplicar(novo);
      recarregarQuadro(novo.contentBase);
    },
    [aplicar, recarregarQuadro],
  );

  async function trocarEntrada(entryPath) {
    try {
      const { site: novo } = await api.sites.save(id, { entryPath });
      notify('Porta de entrada alterada.');
      aplicar(novo);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  async function apagar() {
    try {
      await api.sites.remove(id);
      notify('Projeto apagado.');
      navigate('/artefatos');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
      setApagando(false);
    }
    return undefined;
  }

  if (loading) {
    return (
      <AppShell>
        <p className="state-message">Carregando…</p>
      </AppShell>
    );
  }

  if (error || !site) {
    return (
      <AppShell>
        <div className="state-message error" role="alert">
          <p>{error ?? 'Projeto não encontrado.'}</p>
          <Link className="button secondary" to="/artefatos">
            Voltar para as minhas páginas
          </Link>
        </div>
        <Toast />
      </AppShell>
    );
  }

  const paginas = (site.files ?? []).filter(ehPagina);
  const temPagina = paginas.length > 0 && !site.entryMissing;

  /**
   * Projeto recém-criado, ainda sem nenhum arquivo.
   *
   * ## Por que a tela é outra, e não a mesma com campos vazios
   *
   * Reclamação de usuários em 19/10: ao criar um projeto, a tela já mostrava
   * endereço, quem pode ver e apagar — tudo antes de existir qualquer coisa
   * para endereçar, mostrar ou apagar. Um endereço que ainda não abre nada é
   * ruído; pior, convida a copiá-lo e mandar para alguém.
   *
   * Aqui há **uma coisa para fazer**, e só ela aparece. O resto surge quando
   * passa a significar algo — que é o momento em que o primeiro arquivo entra.
   */
  const vazio = (site.files ?? []).length === 0;

  if (vazio) {
    return (
      <AppShell>
        <div className="site-detail">
          <Cabecalho site={site} onSaved={aplicar} />

          <section className="site-inicio">
            <h2>Envie os arquivos</h2>
            <p className="site-inicio-lead">
              Um HTML basta. Se o artefato tiver partes separadas, arraste a pasta inteira — os
              caminhos relativos são preservados.
            </p>

            <Arquivos site={site} onChanged={recarregar} grande />

            <p className="field-hint">
              O endereço para compartilhar e as opções de quem pode ver aparecem assim que o
              primeiro arquivo entrar.
            </p>
          </section>

          <Apagar onApagar={apagar} apagando={apagando} setApagando={setApagando} discreto />
        </div>

        <Toast />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="site-detail">
        <Cabecalho site={site} onSaved={aplicar} />

        {site.entryMissing && (
          <div className="site-danger" role="alert">
            <p>
              <strong>Esta página não abre.</strong> O endereço aponta para{' '}
              <code>{site.entryPath}</code>, e não há arquivo com esse nome no projeto.
            </p>
            <p className="field-hint">
              {paginas.length > 0
                ? 'Escolha abaixo, na amostra, qual arquivo deve abrir.'
                : 'Envie um arquivo .html para o projeto.'}
            </p>
          </div>
        )}

        <div className="site-layout">
          {/* ── a amostra ─────────────────────────────────────── */}
          <section className="site-stage">
            <div className="site-stage-bar">
              <div className="section-label">Pré-visualização</div>

              {paginas.length > 1 && (
                <label className="site-entry">
                  {/* Era um rótulo invisível. O seletor sozinho mostrava
                      `index.html` e não dizia o que aquilo decide — quem não
                      construiu a tela lia como "arquivo sendo visto", e não
                      como "arquivo que o endereço abre". */}
                  <span className="site-entry-label">Entrada</span>
                  <select
                    className="input"
                    value={site.entryPath}
                    onChange={(e) => trocarEntrada(e.target.value)}
                  >
                    {!paginas.some((a) => a.path === site.entryPath) && (
                      <option value={site.entryPath}>{site.entryPath} (ausente)</option>
                    )}
                    {paginas.map((a) => (
                      <option key={a.path} value={a.path}>
                        {a.path}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* Saiu o botão Atualizar: a amostra se atualiza sozinha quando
                  o conteúdo muda, e um botão que repete o que já acontece só
                  faz duvidar se acontece mesmo.

                  No lugar vêm as duas coisas que o painel de endereço
                  oferecia, aqui onde se está quando se quer mandar o link. */}
              <div className="site-stage-acoes">
                <button type="button" className="button ghost" onClick={() => copiarEndereco(site)}>
                  Copiar endereço
                </button>
                {/* `rel="noreferrer"` para a página publicada não receber o
                    endereço do painel no `Referer`. Ela é conteúdo de usuário;
                    não precisa saber de onde foi aberta. */}
                <a className="button secondary" href={site.path} target="_blank" rel="noreferrer">
                  Abrir
                </a>
              </div>
            </div>

            {temPagina && quadro ? (
              <iframe
                // A chave inclui a entrada e o selo: trocar de arquivo ou
                // reenviar remonta o quadro, em vez de deixar o navegador
                // decidir se reaproveita o que já tem.
                key={`${site.entryPath}:${quadro.selo}`}
                className="site-preview"
                title={`Pré-visualização de ${site.name}`}
                // O endereço vem do `quadro`, FIXADO, e não de `site`: o
                // crachá do projeto é reassinado a cada leitura da API, e ler
                // dali recarregava a página embaixo de quem estava lendo.
                src={urlDoConteudo(site, quadro.selo, quadro.base)}
                // O MESMO isolamento da página publicada, e `allow-same-origin`
                // nunca aqui: com ele o HTML do usuário rodaria na nossa origem,
                // dentro de uma tela autenticada. Ver o cabeçalho deste arquivo.
                sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="site-preview is-empty">
                <p>
                  Envie um arquivo <code>.html</code> para ver a página aqui.
                </p>
              </div>
            )}
          </section>

          {/* ── os controles ───────────────────────────────────────
              A ordem é a do uso, e mudou em 19/10: antes abria pelo
              endereço, que é a última coisa que interessa. Agora segue o
              caminho de quem está trabalhando — mexer nos arquivos, decidir
              quem vê, e só então pegar o link para mandar. */}
          <aside className="site-side">
            <Arquivos site={site} onChanged={recarregar} />
            <Compartilhamento site={site} onSaved={aplicarConteudo} />
            <Apagar onApagar={apagar} apagando={apagando} setApagando={setApagando} />
          </aside>
        </div>
      </div>

      <Toast />
    </AppShell>
  );
}
